import { Controller } from "@hotwired/stimulus";
import {
  type Appearance,
  applyAppearance,
  readAppearance,
  syncAppearanceCookie,
  writeAppearance,
} from "../lib/appearance";

/*
 * The Appearance picker in the menu.
 *
 * A native <select> stretched invisibly over the whole row, which is why this has two targets
 * rather than one: the control holds the answer and the span under it shows the answer, and
 * nothing syncs them for free.
 *
 * The server cannot mark the chosen option, because the choice is a device preference in
 * localStorage that it has never seen. So no option is `selected` in the markup and the value
 * span is left empty; both are filled here on connect.
 *
 * It does NOT apply the stored appearance on connect. The inline script in <head> has already
 * done that, before the first paint; doing it again here would be a second source of truth for
 * the same attribute, and the one that ran later would win.
 */
export default class extends Controller<HTMLElement> {
  static override targets = ["select", "value"];

  declare readonly selectTarget: HTMLSelectElement;
  declare readonly valueTarget: HTMLElement;

  override connect(): void {
    /* The server reads the choice from a cookie to pick the right half of an image that has two.
       A reader who chose before that cookie existed has the choice in localStorage and nothing
       in the cookie, and would otherwise keep the wrong image until they touched this menu. */
    syncAppearanceCookie();

    this.selectTarget.value = readAppearance();
    this.render();
  }

  choose(): void {
    const appearance = this.selectTarget.value as Appearance;

    writeAppearance(appearance);
    applyAppearance(appearance);
    this.render();
  }

  /**
   * The row's visible value, read off the chosen option's own text.
   *
   * Not from a map of names held here: the three are translated, and copy that lives in a
   * controller is copy no translator ever sees.
   */
  private render(): void {
    this.valueTarget.textContent =
      this.selectTarget.options[this.selectTarget.selectedIndex]?.text ?? "";
  }
}
